import { useState, useMemo, SyntheticEvent } from "react";
import { Bar } from "react-chartjs-2";
import ClearIcon from "@mui/icons-material/Clear";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Box,
  Tab,
  Tooltip as MuiTooltip,
  OutlinedInput,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Typography as T,
  alpha,
} from "@mui/material";
import {
  SiteDetails,
  ParticipantBreakdown,
  Histology4Digit,
} from "catalog/types/interfaces/conceptResponse";
import { DEFAULT_OPTS, splitAxisLabel } from "catalog/utils/chart";
import { ChartOptions } from "chart.js";
import { merge } from "chart.js/helpers";
import { SearchIcon } from "~/assets/icons";
import ContainedTabs from "~/components/ContainedTabs";
import FlatCard from "~/components/FlatCard";
import { ExpandableListItem } from "~/components/ListCard";
import InfoIcon from "~/pages/catalog/components/InfoIcon";
import theme from "~/theme";

const HISTOLOGY_BREAKDOWN_OPTS: ChartOptions<"bar"> = merge<
  ChartOptions<"bar">,
  ChartOptions<"bar">
>(
  {
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        title: {
          display: true,
          text: "Participant Count",
        },
        ticks: {
          callback: (value) => {
            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        displayColors: false,
      },
    },
  },
  DEFAULT_OPTS
);

const CODE_GROUPS: { label: string; histologies: string[] }[] = [
  { label: "ductal", histologies: ["8500", "8541", "8534"] },
  { label: "lobular", histologies: ["8520"] },
  { label: "mixed (ductal & lobular)", histologies: ["8521", "8522"] },
  { label: "medullary", histologies: ["8510", "8512", "8513"] },
  { label: "tubular", histologies: ["8211"] },
  { label: "papillary", histologies: ["8260", "8503", "8504", "8509"] },
  { label: "mucinous", histologies: ["8480"] },
];

const numFormatter = new Intl.NumberFormat("en");
const compactFormatter = new Intl.NumberFormat("en", { notation: "compact" });

const formatCountLabel = (participantCount: number, totalCount: number) => {
  const participantPercent = Math.round((participantCount / totalCount) * 100);
  if (!participantCount) {
    return "(none)";
  } else {
    return `${numFormatter.format(participantCount)} (${participantPercent || "<1"}%)`;
  }
};

const HistologyBreakdownChart = (props: { data: Histology4Digit }) => {
  const [chartTab, setChartTab] = useState<"race" | "gender">("gender");
  const getChartData = (data: ParticipantBreakdown[]) => {
    return {
      labels: data.map(({ concept_name }) => splitAxisLabel(concept_name, "or")),
      datasets: [
        {
          label: "Participant Count",
          data: data.map(({ participant_count }) => participant_count),
        },
      ],
    };
  };

  return (
    <TabContext value={chartTab}>
      <Box display="flex" justifyContent="center" gap={2}>
        <ContainedTabs
          value={chartTab}
          onChange={(_: SyntheticEvent, value: any) => setChartTab(value)}
        >
          <Tab label="Gender" value="gender" />
          <Tab label="Race" value="race" />
        </ContainedTabs>
      </Box>
      <TabPanel value="race" className="chart-container">
        <Bar data={getChartData(props.data.race_counts)} options={HISTOLOGY_BREAKDOWN_OPTS} />
      </TabPanel>
      <TabPanel value="gender" className="chart-container">
        <Bar data={getChartData(props.data.gender_counts)} options={HISTOLOGY_BREAKDOWN_OPTS} />
      </TabPanel>
    </TabContext>
  );
};

/**
 * Group histologies into predefined buckets.
 */
const collateHistologies = (histologies: Histology4Digit[], search: string) => {
  const collatedHistologies: {
    label: string;
    histologies: Histology4Digit[];
    totalParticipantCount: number;
  }[] = [];

  histologies.forEach((histology) => {
    let collated = false;

    const { histological_group_name: name, histology_code: code } = histology;
    const query = search.toLowerCase();

    // Only process histologies that match search query
    const matched =
      name.toLocaleLowerCase().includes(query) || code.toLocaleLowerCase().includes(query);

    if (matched) {
      for (const group of CODE_GROUPS) {
        if (group.histologies.find((c) => c === code.split("/")[0])) {
          const label = group.label;
          const found = collatedHistologies.find((h) => h.label === label);
          if (!found) {
            collatedHistologies.push({
              label,
              histologies: [histology],
              totalParticipantCount: histology.participant_count,
            });
          } else {
            found.histologies.push(histology);
            found.totalParticipantCount += histology.participant_count;
          }
          collated = true;
          break;
        }
      }

      // Histology code wasn't found in a predefined group list, add to "other" bucket
      if (!collated) {
        const other = collatedHistologies.find((h) => h.label === "other");
        if (!other) {
          collatedHistologies.push({
            label: "other",
            histologies: [histology],
            totalParticipantCount: histology.participant_count,
          });
        } else {
          other.histologies.push(histology);
          other.totalParticipantCount += histology.participant_count;
        }
      }
    }
  });

  return collatedHistologies;
};

const SearchInput = ({
  search,
  onChange,
  onClear,
}: {
  search: string;
  onChange: (value: string) => void;
  onClear: () => void;
}) => (
  <OutlinedInput
    name="histology-search"
    size="small"
    sx={{ pl: 1.5, mb: 1 }}
    placeholder="Search"
    value={search}
    onChange={(e) => onChange(e.target.value)}
    startAdornment={
      <Box display="flex" alignItems="center" justifyContent="center">
        <SearchIcon width={16} />
      </Box>
    }
    endAdornment={
      search ? (
        <Box display="flex" alignItems="center" justifyContent="center">
          <IconButton size="small" sx={{ svg: { fontSize: 18 } }} onClick={onClear}>
            <ClearIcon />
          </IconButton>
        </Box>
      ) : null
    }
    inputProps={{
      "aria-label": "histology search",
    }}
  />
);

const HistologiesList = (props: { data: SiteDetails }) => {
  const { data } = props;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [search, setSearch] = useState<string>("");

  const histologies = useMemo(
    () =>
      data.histology_4_digit_code_breakdown?.sort(
        (a, b) => b.participant_count - a.participant_count
      ),
    [data]
  );

  const collatedHistologies = useMemo(
    () => collateHistologies(histologies, search),
    [histologies, search]
  );

  const sortedCollatedHistologies = collatedHistologies.sort(
    (a, b) => b.totalParticipantCount - a.totalParticipantCount
  );

  const showEmpty = !sortedCollatedHistologies.length;
  const showList = !showEmpty;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setSelectedIndex(0);
  };

  const handleSearchClear = () => {
    setSearch("");
    setSelectedIndex(0);
  };

  return (
    <FlatCard sx={{ p: 2 }}>
      <Box width="100%">
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5} mb={2}>
          <Box display="flex" alignItems="center" gap={0.75}>
            <T variant="body1" fontWeight={600} whiteSpace="nowrap">
              Histologies
            </T>
            <MuiTooltip
              arrow
              title="Breakdown of cancer incidences by four-digit histological group codes."
            >
              <InfoIcon />
            </MuiTooltip>
          </Box>
          <T
            variant="body2"
            sx={{
              opacity: 0.5,
              fontWeight: 500,
              pr: 1,
            }}
          >
            Total Participants
          </T>
        </Box>

        <Box display="flex" gap={2}>
          {/**
           * Histology groups
           */}
          <List sx={{ flex: "0 0 200px", py: 0 }}>
            <SearchInput
              search={search}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
            />
            {sortedCollatedHistologies.map((group, index) => (
              <ListItem sx={{ textTransform: "capitalize", p: 0 }} key={group.label}>
                <ListItemButton
                  sx={{
                    borderRadius: "6px",
                    mb: 0.5,
                    "&.Mui-selected": {
                      backgroundColor: `${alpha(theme.palette.primary.main, 0.1)}`,
                      span: {
                        color: `${theme.palette.primary.main}`,
                        fontWeight: 500,
                      },
                    },
                  }}
                  selected={index === selectedIndex}
                  onClick={() => setSelectedIndex(index)}
                >
                  <ListItemText
                    sx={{
                      "& .MuiTypography-root": { fontSize: 14 },
                    }}
                  >
                    {group.label} ({numFormatter.format(group.totalParticipantCount)})
                  </ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          {/**
           * Histologies for selected group
           */}
          <List
            sx={{
              flexGrow: 1,
              p: 1,
              borderRadius: "6px",
              border: "1px solid",
              borderColor: "common.cardBorder",
              height: 600,
              overflowY: "auto",
            }}
          >
            {showEmpty && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  opacity: 0.6,
                }}
              >
                No histologies
              </Box>
            )}

            {showList && (
              <>
                {sortedCollatedHistologies[selectedIndex].histologies.map((histology, i) => {
                  return (
                    <ExpandableListItem
                      key={histology.histological_group_name + "-" + i}
                      expansionContent={<HistologyBreakdownChart data={histology} />}
                      rootStyles={{ px: 0.5 }}
                    >
                      <T variant="body2" fontWeight={500} flex={1} py={0.5}>
                        {histology.histological_group_name} ({histology.histology_code})
                      </T>
                      <Box display="flex" alignItems="center" gap={0.5} fontSize={18}>
                        <T
                          variant="body2"
                          sx={{ opacity: 0.67, display: "flex", alignItems: "center", gap: 0.5 }}
                        >
                          {formatCountLabel(
                            histology.participant_count,
                            data.participant_count ?? 0
                          )}
                        </T>
                      </Box>
                    </ExpandableListItem>
                  );
                })}
              </>
            )}
          </List>
        </Box>
      </Box>
    </FlatCard>
  );
};

export default HistologiesList;
